import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LANGUAGE_LABELS, SUPPORTED_LANGUAGES, loadLanguage, type SupportedLanguage } from "@/i18n";
import { cn } from "@/lib/utils";

type Props = {
  variant?: "ghost" | "outline";
  size?: "sm" | "default" | "icon";
  className?: string;
  showLabel?: boolean;
};

export function LanguageSwitcher({
  variant = "ghost",
  size = "sm",
  className,
  showLabel = true,
}: Props) {
  const { i18n, t } = useTranslation();
  // Avoid SSR/CSR hydration mismatch: server renders the fallback ("pt"),
  // and the first client render must match. After hydration, switch to the
  // detected language from localStorage / navigator.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    setHydrated(true);
  }, []);

  const detected = (i18n.resolvedLanguage ?? i18n.language ?? "pt") as SupportedLanguage;
  const current: SupportedLanguage = hydrated
    ? SUPPORTED_LANGUAGES.includes(detected)
      ? detected
      : "pt"
    : "pt";
  const labelText = hydrated ? t("common.language") : "Idioma";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={cn("gap-2", className)}
          aria-label={`${labelText}: ${current.toUpperCase()}`}
          suppressHydrationWarning
        >
          <Globe className="h-4 w-4" />
          {showLabel && (
            <span className="text-xs font-medium uppercase" suppressHydrationWarning>
              {current}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel className="text-xs">{labelText}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SUPPORTED_LANGUAGES.map((lng) => (
          <DropdownMenuItem
            key={lng}
            onClick={() => {
              void loadLanguage(lng).then(() => i18n.changeLanguage(lng));
              if (typeof window !== "undefined") {
                try {
                  window.localStorage.setItem("app.lang", lng);
                } catch {
                  // ignore storage errors (private mode, etc.)
                }
              }
              if (typeof document !== "undefined") {
                document.documentElement.lang = lng;
              }
            }}
            className="flex items-center justify-between"
          >
            <span>{LANGUAGE_LABELS[lng]}</span>
            {current === lng && <Check className="h-3.5 w-3.5 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
